/* Recipes listing filter. Added by Oleg @ IAKOE.
   <ts-recipes-grid>: one dropdown per facet (Product/Occasion/Ease) with
   multi-select checkboxes + an inline search input, filtering EVERY rendered
   card (data-* attrs). Selected values show as removable chips; the featured
   hero hides as soon as any filter/search is active. Load more reveals the
   matched set in pages.
   Liquid renders only page 1; if paginate reports more pages (data-pages), the
   rest are fetched in the background via the Section Rendering API and appended
   hidden, so the filter still sees the whole catalog past the single-page ceiling.
   On mobile the dropdowns open as a full-screen drawer (Filter trigger) and
   the search input is revealed by the Search trigger; same markup, same logic.
   The selection lives in the page URL (?product=a,b&occasion=..&ease=..&q=..): every
   change rewrites it in place (replaceState, so Back is not filled with filter steps)
   and a load reads it back, so returning from a recipe brings the same selection, and a
   filtered view can be shared as a link. Clear all empties it. Other query parameters
   (utm and the like) are kept. */
import { DialogFocus, listen } from 'ia-drawer';

if (!customElements.get('ts-recipes-grid')) {
  customElements.define(
    'ts-recipes-grid',
    class TsRecipesGrid extends HTMLElement {
      connectedCallback() {
        this.perPage = parseInt(this.dataset.perPage, 10) || 9;
        this.shown = this.perPage;
        this.cards = Array.from(this.querySelectorAll('[data-card]'));
        this.checks = Array.from(this.querySelectorAll('[data-facet]'));
        this.search = this.querySelector('[data-search]');
        this.searchBox = this.querySelector('[data-search-box]');
        this.searchClear = this.querySelector('[data-search-clear]');
        this.chips = this.querySelector('[data-chips]');
        this.featured = this.querySelector('[data-featured]');
        this.dupeCard = this.querySelector('[data-featured-dupe]');
        this.filtersPanel = this.querySelector('[data-filters-panel]');
        this.dds = Array.from(this.querySelectorAll('[data-dd]'));
        this.loadMore = this.querySelector('[data-load-more]');
        this.empty = this.querySelector('[data-empty]');
        this.grid = this.querySelector('[data-grid]');
        this.sectionId = this.dataset.sectionId;
        this.totalPages = parseInt(this.dataset.pages, 10) || 1;
        this.pageParam = this.dataset.pageParam || 'page';
        this.loader = new AbortController();
        this.filters = { product: new Set(), occasion: new Set(), ease: new Set() };
        this.query = '';

        // Desktop: each facet is an exclusive popover (open one closes the rest).
        // Mobile (drawer): each facet just expands/collapses on click, independently.
        this.dds.forEach((dd) => {
          const btn = dd.querySelector('[data-dd-toggle]');
          if (!btn) return;
          btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const open = dd.classList.contains('is-open');
            if (window.matchMedia('(min-width: 1024px)').matches) {
              this.closeDropdowns();
              if (!open) this.openDropdown(dd);
            } else if (open) {
              this.closeDropdown(dd);
            } else {
              this.openDropdown(dd);
            }
          });
        });
        // Outside-click only collapses the desktop popovers; the mobile drawer stays as left.
        this._onDocClick = (e) => {
          if (!window.matchMedia('(min-width: 1024px)').matches) return;
          if (!e.target.closest('[data-dd]')) this.closeDropdowns();
        };
        document.addEventListener('click', this._onDocClick);

        // Mobile: Filter trigger opens the dropdowns as a drawer; close (×) and View dismiss it.
        const openBtn = this.querySelector('[data-filter-open]');
        if (openBtn) openBtn.addEventListener('click', () => this.openDrawer());
        if (this.filtersPanel) this.drawerFocus = new DialogFocus(this.filtersPanel);
        this.drawerListeners = new AbortController();
        listen(document, 'keydown', (e) => {
          if (e.key === 'Escape' && this.filtersPanel?.classList.contains('is-open')) this.closeDrawer();
        }, this.drawerListeners.signal);
        this.querySelectorAll('[data-filter-close]').forEach((b) =>
          b.addEventListener('click', () => this.closeDrawer())
        );

        this.checks.forEach((c) =>
          c.addEventListener('change', () => {
            this.filters[c.dataset.facet][c.checked ? 'add' : 'delete'](c.value);
            this.shown = this.perPage;
            this.apply();
            // Desktop: collapse the dropdown once a choice is made.
            if (window.matchMedia('(min-width: 1024px)').matches) this.closeDropdowns();
          })
        );
        if (this.search)
          this.search.addEventListener('input', () => {
            // Only start matching from the 3rd character; shorter input is treated as empty.
            const v = this.search.value.trim().toLowerCase();
            this.query = v.length >= 3 ? v : '';
            this.shown = this.perPage;
            this.syncSearchIcon();
            this.apply();
          });
        if (this.searchClear)
          this.searchClear.addEventListener('click', () => {
            this.search.value = '';
            this.query = '';
            this.shown = this.perPage;
            this.syncSearchIcon();
            this.apply();
            this.search.focus();
          });
        if (this.loadMore)
          this.loadMore.addEventListener('click', () => {
            this.shown += this.perPage;
            this.apply();
          });
        if (this.chips)
          this.chips.addEventListener('click', (e) => {
            const chip = e.target.closest('[data-clear]');
            if (!chip) return;
            if (chip.dataset.clear === 'all') this.reset();
            else {
              const [facet, value] = chip.dataset.clear.split(':');
              this.filters[facet].delete(value);
              const box = this.checks.find((c) => c.dataset.facet === facet && c.value === value);
              if (box) box.checked = false;
            }
            this.shown = this.perPage;
            this.apply();
          });

        this.restoreFromUrl();
        this.apply();

        // Liquid renders only page 1 of the paginate. If Shopify reports more pages
        // (data-pages > 1), pull the rest in the background via the Section Rendering
        // API and append them hidden, so search/filter see the whole catalogue past the
        // single-page ceiling. Keyed off paginate.pages (the real count) — not a fixed
        // page size — so it stays correct whatever the effective page size is.
        if (this.sectionId && this.grid && this.totalPages > 1) {
          this.loadRest(2);
        }
      }

      async loadRest(page) {
        let added = 0;
        try {
          // The page parameter name comes from paginate.page_param: a metaobject list is
          // paginated under its own key, and a plain `page` would return page 1 again.
          const url = new URL(location.pathname, location.origin);
          url.searchParams.set('section_id', this.sectionId);
          url.searchParams.set(this.pageParam, page);
          const res = await fetch(url, { signal: this.loader.signal });
          if (!res.ok) return;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const seen = new Set(this.cards.map((c) => c.dataset.key));
          doc.querySelectorAll('[data-card]').forEach((c) => {
            if (seen.has(c.dataset.key)) return;
            seen.add(c.dataset.key);
            const node = document.importNode(c, true);
            node.hidden = true;
            this.grid.appendChild(node);
            added += 1;
          });
          this.cards = Array.from(this.querySelectorAll('[data-card]'));
          this.apply();
        } catch {
          // Network hiccup or the section was replaced: page 1 stays usable, stop fetching.
          return;
        }
        // A page with nothing new means the server answered a page we already have: stop.
        if (added && page < this.totalPages) this.loadRest(page + 1);
      }

      disconnectedCallback() {
        this.loader.abort();
        document.removeEventListener('click', this._onDocClick);
        document.documentElement.classList.remove('ts-recipes-locked');
        this.drawerListeners?.abort();
        this.drawerFocus?.deactivate({ returnFocus: false });
      }

      openDrawer() {
        if (!this.filtersPanel) return;
        this.filtersPanel.classList.add('is-open');
        document.documentElement.classList.add('ts-recipes-locked');
        const t = this.querySelector('[data-filter-open]');
        if (t) t.setAttribute('aria-expanded', 'true');
        // Dialog semantics only while it is the mobile drawer; on desktop the panel is the inline bar.
        this.filtersPanel.setAttribute('role', 'dialog');
        this.filtersPanel.setAttribute('aria-modal', 'true');
        this.drawerFocus.activate(t);
      }

      closeDrawer() {
        if (!this.filtersPanel) return;
        this.filtersPanel.classList.remove('is-open');
        document.documentElement.classList.remove('ts-recipes-locked');
        const t = this.querySelector('[data-filter-open]');
        if (t) t.setAttribute('aria-expanded', 'false');
        this.filtersPanel.removeAttribute('role');
        this.filtersPanel.removeAttribute('aria-modal');
        this.drawerFocus.deactivate();
      }

      openDropdown(dd) {
        dd.classList.add('is-open');
        const panel = dd.querySelector('[data-dd-panel]');
        const btn = dd.querySelector('[data-dd-toggle]');
        if (panel) panel.hidden = false;
        if (btn) btn.setAttribute('aria-expanded', 'true');
      }

      closeDropdown(dd) {
        dd.classList.remove('is-open');
        const panel = dd.querySelector('[data-dd-panel]');
        const btn = dd.querySelector('[data-dd-toggle]');
        if (panel) panel.hidden = true;
        if (btn) btn.setAttribute('aria-expanded', 'false');
      }

      closeDropdowns() {
        this.dds.forEach((dd) => this.closeDropdown(dd));
      }

      isActive() {
        return (
          this.query !== '' ||
          ['product', 'occasion', 'ease'].some((f) => this.filters[f].size > 0)
        );
      }

      matches(card) {
        for (const facet of ['product', 'occasion', 'ease']) {
          const set = this.filters[facet];
          if (set.size) {
            const vals = (card.dataset[facet] || '').split(' ');
            if (![...set].some((v) => vals.includes(v))) return false;
          }
        }
        if (this.query && !(card.dataset.search || '').includes(this.query)) return false;
        return true;
      }

      // The selection as it was left in the URL: facet values that still exist as a
      // checkbox are ticked (unknown ones are dropped), the search text goes back into
      // the field.
      restoreFromUrl() {
        const params = new URLSearchParams(location.search);
        // The URL is the only truth: a browser that restores form fields on Back could
        // otherwise leave a box ticked that no filter backs.
        this.checks.forEach((c) => (c.checked = false));
        if (this.search) this.search.value = '';
        ['product', 'occasion', 'ease'].forEach((facet) => {
          (params.get(facet) || '').split(',').filter(Boolean).forEach((value) => {
            const box = this.checks.find((c) => c.dataset.facet === facet && c.value === value);
            if (!box) return;
            box.checked = true;
            this.filters[facet].add(value);
          });
        });
        const q = (params.get('q') || '').trim();
        if (q && this.search) {
          this.search.value = q;
          this.query = q.length >= 3 ? q.toLowerCase() : '';
        }
        this.syncSearchIcon();
      }

      // Writes the selection into the URL in place; nothing else in it changes.
      syncUrl() {
        const url = new URL(location.href);
        ['product', 'occasion', 'ease'].forEach((facet) => {
          const values = [...this.filters[facet]];
          if (values.length) url.searchParams.set(facet, values.join(','));
          else url.searchParams.delete(facet);
        });
        const text = this.search ? this.search.value.trim() : '';
        if (text.length >= 3) url.searchParams.set('q', text);
        else url.searchParams.delete('q');
        if (url.href !== location.href) history.replaceState(history.state, '', url);
      }

      reset() {
        this.filters = { product: new Set(), occasion: new Set(), ease: new Set() };
        this.query = '';
        this.checks.forEach((c) => (c.checked = false));
        if (this.search) this.search.value = '';
        this.syncSearchIcon();
      }

      // Show the × (clear) once the field has any text, otherwise the magnifier.
      syncSearchIcon() {
        if (this.searchBox) this.searchBox.classList.toggle('is-filled', !!(this.search && this.search.value));
      }

      labelFor(facet, value) {
        const input = this.checks.find((c) => c.dataset.facet === facet && c.value === value);
        // the LAST span is the text label (the first span is the .ts-recipes__box swatch)
        const span = input && input.parentElement.querySelector('span:last-child');
        return span ? span.textContent : value;
      }

      apply() {
        if (this.featured) this.featured.hidden = this.isActive();
        let matched = this.cards.filter((c) => this.matches(c));
        // while the featured block shows the fallback-first recipe, keep its duplicate
        // card out of the grid; once filtering/search hides the featured, let it back in.
        if (this.dupeCard && this.featured && !this.featured.hidden) {
          matched = matched.filter((c) => c !== this.dupeCard);
        }
        this.cards.forEach((c) => (c.hidden = true));
        matched.slice(0, this.shown).forEach((c) => (c.hidden = false));
        if (this.loadMore) this.loadMore.hidden = matched.length <= this.shown;
        if (this.empty) this.empty.hidden = matched.length !== 0;
        this.renderChips();
        this.syncUrl();
      }

      renderChips() {
        if (!this.chips) return;
        const out = [];
        ['product', 'occasion', 'ease'].forEach((f) =>
          this.filters[f].forEach((v) =>
            out.push(`<button type="button" class="ts-recipes__chip" data-clear="${f}:${v}"><span>${this.labelFor(f, v)}</span><svg class="ts-recipes__chip-x" width="7" height="7" viewBox="0 0 7 7" fill="none" aria-hidden="true"><path d="M1 1l5 5M6 1L1 6" stroke="currentColor" stroke-linecap="round"/></svg></button>`)
          )
        );
        if (out.length) {
          out.push('<button type="button" class="ts-recipes__chip ts-recipes__chip--clear" data-clear="all">Clear all</button>');
          this.chips.innerHTML = out.join('');
          this.chips.hidden = false;
        } else {
          this.chips.innerHTML = '';
          this.chips.hidden = true;
        }
      }
    }
  );
}
